// 相対 import('./cart-passport-logic.js') は ?v= なしの URL になり、Shopify CDN に1年キャッシュされた
// 旧版(クリックポスト1000g/¥185)が配信され続けていた(2026-10-12 #2259 調査で判明)。
// theme.liquid の modulepreload が持つ版付き URL(asset_url)から読み込む。
const logicUrl =
  document.querySelector('link[rel="modulepreload"][href*="/cart-passport-logic.js"]')?.href ||
  new URL('./cart-passport-logic.js', import.meta.url).href;
const {
  totalWeight,
  determineShippingMode,
  remainingCapacity,
  SHIPPING_MODES,
} = await import(logicUrl);

// Shopify 配送設定「宅急便 100g以上」(cart-passport.liquid の total_g >= 100 と同じ)
const TAKKYUBIN_SELECTABLE_MIN_GRAMS = 100;
const BADGE_ICON_MAP = { clickpost: '✉️', takkyubin: '🚚', oversize: '📮' };
const BADGE_LABEL_MAP = {
  clickpost: 'メール便',
  takkyubin: '宅急便',
  oversize: 'お問い合わせください',
};

// ─── Rise テーマのカート変更を捕捉 ───────────────────────────
// Rise は内部 pubsub.js（publish('cart-update')）でカート変更を通知するが、
// pubsub.js は ES module ではなく、import から購読できない。
// そこで fetch() を monkey-patch して /cart/change|update|add|clear の完了を捕捉し、
// 統一的に CustomEvent 'cart:updated' を発火する。
// （既に発火している箇所と二重発火しても _scheduleRefresh が debounce で吸収）
(function patchCartFetch() {
  if (window.__cartPassportFetchPatched) return;
  window.__cartPassportFetchPatched = true;
  const origFetch = window.fetch;
  if (typeof origFetch !== 'function') return;
  const CART_MUTATION_RE = /\/cart\/(change|update|add|clear)(\.js)?(\?|$)/;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const result = origFetch.apply(this, arguments);
    if (CART_MUTATION_RE.test(url)) {
      result.then((res) => {
        if (res && res.ok) {
          // サーバー反映を待ってから通知
          setTimeout(() => {
            document.dispatchEvent(new CustomEvent('cart:updated'));
          }, 80);
        }
      }).catch(() => { /* noop */ });
    }
    return result;
  };
})();

class CartPassportController {
  constructor(root) {
    this.root = root;
    this.context = root.classList.contains('cart-passport--minicart') ? 'minicart' : 'cart';
    this.previousMode = root.dataset.mode;
    this._refreshTimer = null;
    this._pendingAdds = 0; // 同時実行追加数
    this.bindEvents();
  }

  bindEvents() {
    this.root.addEventListener('click', (e) => {
      // 追加ボタン
      const btn = e.target.closest('[data-cart-passport-add]');
      if (btn) { e.preventDefault(); this.handleAddClick(btn); return; }

      // 数量 −
      const minus = e.target.closest('[data-qty-minus]');
      if (minus) { this.adjustQty(minus.closest('.cart-passport-recommend__card'), -1); return; }

      // 数量 ＋
      const plus = e.target.closest('[data-qty-plus]');
      if (plus) { this.adjustQty(plus.closest('.cart-passport-recommend__card'), 1); return; }
    });

    // バリアント変更 → カードの data-weight 更新 + 追加ボタンの有効/無効切替
    this.root.addEventListener('change', (e) => {
      // Rise の cart-drawer-items / cart-items は配下の change を全て数量入力扱いで検証し
      // 「undefinedの増分でのみ追加できます」を出すため、ここで止める
      e.stopPropagation();
      const select = e.target.closest('.cart-passport-recommend__variant-select');
      if (!select) return;
      const card = select.closest('.cart-passport-recommend__card');
      if (!card) return;
      const opt = select.options[select.selectedIndex];
      const variantId = opt ? opt.value : '';
      const weight = opt ? opt.dataset.weight || '0' : '0';

      card.dataset.weight = weight;

      const btn = card.querySelector('.cart-passport-recommend__add');
      if (!btn) return;
      if (variantId) {
        card.dataset.variantId = variantId;
        btn.dataset.variantId = variantId;
        btn.disabled = false;
        btn.removeAttribute('aria-disabled');
        if (btn.classList.contains('cart-passport-recommend__add--needs-variant')) {
          btn.textContent = '+ 追加';
          btn.classList.remove('cart-passport-recommend__add--needs-variant');
        }
      } else {
        delete card.dataset.variantId;
        delete btn.dataset.variantId;
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.textContent = 'サイズを選択';
        btn.classList.add('cart-passport-recommend__add--needs-variant');
      }
    });

    document.addEventListener('cart:updated', () => this._scheduleRefresh());

    document.addEventListener('submit', (e) => {
      if (e.target.matches('form[action="/cart/add"], form[action="/cart"]')) {
        setTimeout(() => this._scheduleRefresh(), 300);
      }
    });
  }

  /** debounce で refresh を集約（race condition 緩和） */
  _scheduleRefresh() {
    clearTimeout(this._refreshTimer);
    this._refreshTimer = setTimeout(() => this.refresh(), 150);
  }

  /** _refreshCartDrawer は削除済み（drawer DOM 上書きが Rise の close 動作を壊すため） */

  async handleAddClick(btn) {
    const card = btn.closest('.cart-passport-recommend__card');
    const select = card?.querySelector('.cart-passport-recommend__variant-select');
    const qtyInput = card?.querySelector('.cart-passport-recommend__qty-input');
    const variantId = select ? parseInt(select.value, 10) : parseInt(btn.dataset.variantId, 10);
    const quantity = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;

    if (!Number.isFinite(variantId)) return;
    btn.disabled = true;
    const original = btn.textContent;
    btn.textContent = '...';

    // 楽観的更新前のスナップショット（失敗時の巻き戻し用）
    const snapshot = {
      totalGrams: this.root.dataset.totalGrams,
      mode: this.root.dataset.mode,
      cardDisplay: card ? card.style.display : null,
    };

    if (card) this._optimisticUpdate(card, quantity);
    this._pendingAdds++;

    try {
      const drawer = this.context === 'minicart' ? document.querySelector('cart-drawer') : null;
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity }],
          ...(drawer ? { sections: 'cart-drawer,cart-icon-bubble', sections_url: location.pathname } : {}),
        }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.description || `add failed (${res.status})`);
      }
      const state = await res.json().catch(() => null);
      document.dispatchEvent(new CustomEvent('cart:updated'));
      btn.textContent = '✓';
      // ドロワー: 中身(.drawer__inner)だけ差し替えて開いたままにする(reload するとドロワーが閉じる)。
      // 新しいパスポートは MutationObserver の init で再バインドされる。
      // カートページ or 差し替え失敗時は従来どおり reload。
      if (drawer && replaceDrawerSections(drawer, state)) return;
      setTimeout(() => location.reload(), 400);
    } catch (err) {
      console.error('[cart-passport] add failed', err);
      // 完全巻き戻し: totalGrams / mode / メーター / カード表示すべて元に戻す
      this._rollbackOptimistic(snapshot, card);
      btn.textContent = '✕';
      setTimeout(() => {
        btn.textContent = original;
        btn.disabled = false;
      }, 1500);
    } finally {
      this._pendingAdds--;
    }
  }

  adjustQty(card, delta) {
    const input = card?.querySelector('.cart-passport-recommend__qty-input');
    if (!input) return;
    const current = parseInt(input.value, 10) || 1;
    input.value = Math.max(1, Math.min(10, current + delta));
  }

  _optimisticUpdate(card, quantity) {
    const weight = parseFloat(card.dataset.weight) || 0;
    const currentTotal = parseFloat(this.root.dataset.totalGrams) || 0;
    const newTotal = currentTotal + weight * quantity;

    const mode = determineShippingMode(newTotal);
    const cap = remainingCapacity(newTotal, mode);

    this.root.dataset.totalGrams = String(newTotal);
    this.root.dataset.mode = mode.id;
    // oversize は render() と同じくコンテナごと隠す(続く refresh で確定)
    if (mode.id === SHIPPING_MODES.OVERSIZE) {
      this.root.style.display = 'none';
      card.style.display = 'none';
      return;
    }
    this._updateModeDom(mode, newTotal);
    this._updateMeterDom(mode, cap);

    // カードを即時非表示（追加済みなので不要）
    card.style.display = 'none';
  }

  /** 楽観的更新のスナップショットから巻き戻し */
  _rollbackOptimistic(snapshot, card) {
    this.root.dataset.totalGrams = snapshot.totalGrams ?? '0';
    this.root.dataset.mode = snapshot.mode ?? 'clickpost';

    const total = parseFloat(this.root.dataset.totalGrams) || 0;
    const mode = determineShippingMode(total);
    const cap = remainingCapacity(total, mode);
    this.root.style.display = '';
    this._updateModeDom(mode, total);
    this._updateMeterDom(mode, cap);

    if (card && snapshot.cardDisplay !== null) {
      card.style.display = snapshot.cardDisplay;
    }
  }

  /** バッジ・案内文・補足行の更新（render / 楽観的更新 / 巻き戻しで共有） */
  _updateModeDom(mode, totalGrams) {
    const badge = this.root.querySelector('[data-cart-passport-badge]');
    if (badge) {
      badge.dataset.mode = mode.id;
      badge.className = badge.className.replace(
        /cart-passport-badge--(clickpost|takkyubin|oversize)/,
        `cart-passport-badge--${mode.id}`
      );
      const icon = badge.querySelector('.cart-passport-badge__icon');
      if (icon) icon.textContent = BADGE_ICON_MAP[mode.id] ?? '✉️';
      const label = badge.querySelector('.cart-passport-badge__label');
      if (label) label.textContent = BADGE_LABEL_MAP[mode.id] ?? '';
      const price = badge.querySelector('.cart-passport-badge__price');
      if (price) price.textContent = mode.priceLabel;
    }

    const isClickpost = mode.id === SHIPPING_MODES.CLICKPOST;
    // 「この組み合わせは1通/1箱でお届けできます」
    const context = this.root.querySelector('[data-cart-passport-context]');
    if (context) {
      const text = isClickpost ? context.dataset.textClickpost : context.dataset.textTakkyubin;
      if (text) context.textContent = text;
    }
    // 「お手続き画面で宅急便（¥800）もお選びいただけます」: Shopify 配送設定「宅急便 100g以上」
    const selectable = this.root.querySelector('[data-cart-passport-selectable]');
    if (selectable) selectable.hidden = !(isClickpost && totalGrams >= TAKKYUBIN_SELECTABLE_MIN_GRAMS);
    // 宅急便→メール便に戻ったら「宅急便モードに切り替わりました」を消す
    if (isClickpost) this.root.querySelector('.cart-passport__switch-notice')?.remove();
  }

  /** メーター DOM 更新（_optimisticUpdate と render で共有） */
  _updateMeterDom(mode, cap) {
    // メーター親に data-mode を伝播（CSS の表示切替がこれを参照）
    const meter = this.root.querySelector('[data-cart-passport-meter]');
    if (meter) meter.dataset.mode = mode.id;

    // クリックポスト用バー・fill 更新（宅急便時は CSS で非表示）
    if (mode.id === SHIPPING_MODES.CLICKPOST) {
      const bar = this.root.querySelector('.cart-passport-meter__bar');
      if (bar) {
        const near = cap.percentRemaining <= 10;
        bar.classList.toggle('cart-passport-meter__bar--near-limit', near);
        bar.setAttribute('aria-valuenow', String(cap.percentUsed));
        // setProperty で !important を強制（外部 CSS の干渉を防止）
        const barStyles = {
          background: '#ece7d8',
          height: '16px',
          'border-radius': '8px',
          overflow: 'hidden',
          border: '1px solid #d8cfb8',
          position: 'relative',
          display: 'block',
          width: '100%',
          'box-sizing': 'border-box',
        };
        Object.entries(barStyles).forEach(([k, v]) => bar.style.setProperty(k, v, 'important'));
      }
      const fill = this.root.querySelector('.cart-passport-meter__fill');
      if (fill) {
        const gradient = cap.percentRemaining <= 10
          ? 'linear-gradient(90deg,#a3b556 0%,#c47a3b 100%)'
          : 'linear-gradient(90deg,#6b8a1f 0%,#8aa238 100%)';
        const fillStyles = {
          width: `${cap.percentUsed}%`,
          'min-width': '12px',
          height: '100%',
          background: gradient,
          'border-radius': '7px 0 0 7px',
          'box-shadow': 'inset 0 -2px 0 rgba(0,0,0,0.12)',
          transition: 'width 0.4s ease-out',
          display: 'block',
          opacity: '1',
          visibility: 'visible',
          'box-sizing': 'border-box',
        };
        Object.entries(fillStyles).forEach(([k, v]) => fill.style.setProperty(k, v, 'important'));
      }
      const remaining = this.root.querySelector('.cart-passport-meter__remaining');
      if (remaining) {
        remaining.textContent = `残り ${cap.percentRemaining}%`;
      }
    }
    // 宅急便モードの告知は SSR で常に出力済み。表示切替は CSS の [data-mode]
  }

  async refresh() {
    // 追加中は refresh をスキップ（race condition 緩和）
    if (this._pendingAdds > 0) {
      this._scheduleRefresh();
      return;
    }
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) return;
      const cart = await res.json();
      this.render(cart);
    } catch (err) {
      console.error('[cart-passport] refresh failed', err);
    }
  }

  render(cart) {
    const items = cart.items.map((i) => ({
      id: i.product_id,
      variantId: i.variant_id,
      weight: i.grams,
      quantity: i.quantity,
    }));

    const total = totalWeight(items);
    const mode = determineShippingMode(total);
    const cap = remainingCapacity(total, mode);

    this.root.dataset.mode = mode.id;
    this.root.dataset.totalGrams = String(total);

    // oversize に動的遷移したらコンテナごと非表示（SSR 側の挙動と一致）
    if (mode.id === SHIPPING_MODES.OVERSIZE) {
      this.root.style.display = 'none';
      this.previousMode = mode.id;
      return;
    }
    this.root.style.display = '';

    if (this.previousMode === SHIPPING_MODES.CLICKPOST && mode.id === SHIPPING_MODES.TAKKYUBIN) {
      this.showSwitchNotice();
    }
    this.previousMode = mode.id;

    this._updateModeDom(mode, total);
    this._updateMeterDom(mode, cap);

    this.filterRecommendCards(cap.remainingGrams, mode);
  }

  filterRecommendCards(remainingGrams, mode) {
    const cards = this.root.querySelectorAll('.cart-passport-recommend__card');
    let anyVisible = false;

    // oversize モードはレコメンド自体非表示
    if (mode && mode.id === SHIPPING_MODES.OVERSIZE) {
      cards.forEach((card) => { card.style.display = 'none'; });
      return;
    }

    cards.forEach((card) => {
      const weight = parseFloat(card.dataset.weight);
      const fits = Number.isFinite(weight) && weight < remainingGrams;
      // dup フィルタを廃止: カート内にあっても常に提案し続ける
      card.style.display = fits ? '' : 'none';
      if (fits) anyVisible = true;
    });

    // 全カードが重量オーバーで非表示の場合は最軽量カードだけ見せる
    if (!anyVisible && cards.length > 0) {
      let lightestCard = null;
      let lightestWeight = Infinity;
      cards.forEach((card) => {
        const w = parseFloat(card.dataset.weight);
        if (Number.isFinite(w) && w < lightestWeight) {
          lightestWeight = w;
          lightestCard = card;
        }
      });
      if (lightestCard) lightestCard.style.display = '';
    }
  }

  showSwitchNotice() {
    if (this.root.querySelector('.cart-passport__switch-notice')) return;
    const notice = document.createElement('div');
    notice.className = 'cart-passport__switch-notice';
    notice.setAttribute('role', 'status');
    notice.setAttribute('aria-live', 'polite');
    notice.textContent = '✨ 宅急便モードに切り替わりました。もうこの送料を払うなら、もう少しまとめませんか？';
    this.root.prepend(notice);
  }
}

// PDP: バリアント変更時にバッジを更新
// variant.weight はShopify管理画面の重量単位設定に依存する（このストアはグラム設定）
document.addEventListener('variant:change', (e) => {
  const variant = e.detail && e.detail.variant;
  if (!variant) return;
  const wrap = document.querySelector('.cart-passport-badge-wrap');
  if (!wrap) return;
  const badge = wrap.querySelector('[data-cart-passport-badge]');
  if (!badge) return;

  const grams = (Number.isFinite(variant.weight) && variant.weight > 0) ? variant.weight : 0;

  // 重量未設定なら PDP バッジを隠す（誤って "メール便" と表示しない）
  if (grams === 0) {
    wrap.style.display = 'none';
    return;
  }
  wrap.style.display = '';

  const mode = determineShippingMode(grams);
  badge.dataset.mode = mode.id;
  badge.className = badge.className.replace(
    /cart-passport-badge--(clickpost|takkyubin|oversize)/,
    `cart-passport-badge--${mode.id}`
  );

  const icon = badge.querySelector('.cart-passport-badge__icon');
  if (icon) icon.textContent = BADGE_ICON_MAP[mode.id] ?? '✉️';
  const label = badge.querySelector('.cart-passport-badge__label');
  if (label) label.textContent = BADGE_LABEL_MAP[mode.id] ?? '';
  const price = badge.querySelector('.cart-passport-badge__price');
  if (price) price.textContent = mode.priceLabel;
  const hint = wrap.querySelector('[data-cart-passport-hint]');
  if (hint) hint.textContent = mode.id === 'clickpost' ? hint.dataset.hintClickpost : hint.dataset.hintTakkyubin;
});

// miyagen-raffle-cart.js の rerenderCart と同じ Rise 流の差し替え
function replaceDrawerSections(drawer, state) {
  try {
    const html = state && state.sections && state.sections['cart-drawer'];
    const inner = html && new DOMParser().parseFromString(html, 'text/html').querySelector('.drawer__inner');
    const cur = drawer.querySelector('.drawer__inner');
    if (!inner || !cur) return false;
    cur.innerHTML = inner.innerHTML;
    const bubbleHtml = state.sections['cart-icon-bubble'];
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && bubbleHtml) {
      const fresh = new DOMParser().parseFromString(bubbleHtml, 'text/html').querySelector('.shopify-section');
      if (fresh) bubble.innerHTML = fresh.innerHTML;
    }
    return true;
  } catch (e) {
    return false;
  }
}

function init() {
  document.querySelectorAll('[data-cart-passport]').forEach((el) => {
    if (el.dataset.cartPassportInit === '1') return;
    el.dataset.cartPassportInit = '1';
    new CartPassportController(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

document.addEventListener('cart:rendered', init);
// Rise は数量変更などで cart-drawer-items の innerHTML を差し替える(cart:rendered は誰も発火しない)。
// 差し替え後の新しいパスポートにもイベントを張る
new MutationObserver(init).observe(document.documentElement, { childList: true, subtree: true });

/* ─── cart-drawer の close() を override して確実に display:none で消す ───
   CSS の visibility:hidden や class manipulation が不安定なので、
   close() 後に display:none を強制適用、open() 時に display を戻す。 */
function patchCartDrawer() {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer || drawer.__miyagenPatched) return;
  drawer.__miyagenPatched = true;

  const origClose = drawer.close ? drawer.close.bind(drawer) : null;
  const origOpen = drawer.open ? drawer.open.bind(drawer) : null;

  if (origClose) {
    drawer.close = function () {
      try { origClose(); } catch (e) {}
      // 強制クリーンアップ
      this.classList.remove('active', 'animate');
      this.style.display = 'none';
      this.style.visibility = 'hidden';
      this.style.opacity = '0';
      this.style.pointerEvents = 'none';
      document.body.classList.remove('overflow-hidden');
      document.body.style.overflow = '';
    };
  }
  if (origOpen) {
    drawer.open = function (...args) {
      // open 時に display 復活
      this.style.display = '';
      this.style.visibility = '';
      this.style.opacity = '';
      this.style.pointerEvents = '';
      return origOpen(...args);
    };
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', patchCartDrawer);
} else {
  patchCartDrawer();
}
// cart-drawer が後から差し替えられる可能性に備え再パッチ
document.addEventListener('cart:rendered', patchCartDrawer);
